import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  IconButton,
  Tooltip,
  useDisclosure,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiSettings } from 'react-icons/fi';
import { useContactsTable } from '../../../hooks/useContactsTable';
import { useVisibilityScope } from '../../../hooks/useVisibilityScope';
import { useSmartLists } from '../../../hooks/useSmartLists';
import { useCRMTablePreferences } from '../../../hooks/useCRMTablePreferences';
import usePostcardAccess from '../../../hooks/usePostcardAccess';
import CustomizableContactsTable from '../../../components/CRM/CustomizableContactsTable';
import ContactsFilterToolbar from '../../../components/CRM/ContactsFilterToolbar';
import TableCustomizationModal from '../../../components/CRM/TableCustomizationModal';
import BulkContactActionsBar from '../../../components/CRM/BulkContactActionsBar';
import {
  BulkContactTagModal,
  BulkContactTypeModal,
  BulkContactOwnershipModal,
  BulkContactRecordScopeModal,
  BulkDeleteContactsModal,
  BulkExportContactsModal,
  BulkEmailModal,
  BulkSMSModal,
} from '../../../components/CRM/BulkContactModals';
import {
  ALL_CONTACTS_COLUMNS,
  DEFAULT_CONTACTS_PREFERENCES,
} from '../../../components/CRM/contactsTableColumnDefinitions';
import WorkbenchQuickChips from './WorkbenchQuickChips';
import BulkEnrollCampaignModal from './BulkEnrollCampaignModal';
import BulkPostcardModal from '../../../components/CRM/BulkPostcardModal';

/**
 * People lens of the workspace workbench — the Dashboard contacts table
 * experience (same hook, toolbar, table, select-all banner, and bulk save
 * paths), retargeted at the workspace:
 *  - row click opens /crm/workspace/:contactId instead of the drawer
 *  - column prefs live under their own Firestore key, so customizing here
 *    never mutates the classic dashboard's layout
 *  - adds the one bulk action that exists nowhere else: Enroll in Campaign
 */

// Same defaults as the dashboard plus Total Deals — deal context on person rows.
const WORKSPACE_CONTACTS_COLUMNS = [
  'name',
  'types',
  'phone',
  'email',
  'tags',
  'totalDeals',
  'lastContact',
  'createdAt',
];
const WORKSPACE_CONTACTS_PREFERENCES = {
  ...DEFAULT_CONTACTS_PREFERENCES,
  visibleColumns: WORKSPACE_CONTACTS_COLUMNS,
  columnOrder: WORKSPACE_CONTACTS_COLUMNS,
};

export default function PeopleWorkbench({
  currentUser,
  isCRMAdmin,
  selectedUserId,
  crmUsers,
  usersMap,
  dialpadEnabled,
  searchTerm,
  onOpenPerson,
  onCountChange,
}) {
  const toast = useToast();
  const { canSendPostcards } = usePostcardAccess();
  const boxBG = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  /* ---------------- Table preferences (isolated key) ---------------- */
  const {
    preferences: tablePreferences,
    updatePreferences,
    resetToDefaults: resetTablePrefs,
  } = useCRMTablePreferences(currentUser?.uid, {
    defaultPreferences: WORKSPACE_CONTACTS_PREFERENCES,
    preferencesKey: 'workspaceContactsTablePrefs',
  });

  /* ---------------- Server-side contacts table ---------------- */
  // Multi-owner visibility: owned + team book-shares + record shares (+ company
  // records for admins). Admin with no user selected = All Users.
  const { scope: ownerScope } = useVisibilityScope({
    entityType: 'contact',
    targetUserId: isCRMAdmin ? (selectedUserId || null) : currentUser?.uid,
  });

  const {
    contacts,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    loading,
    filters: activeFilters,
    sort: contactsSort,
    applyFilters,
    applySort,
    setPage,
    setPageSize,
    setGlobalSearch,
    refresh: refreshContacts,
    fetchAllIds,
    fetchContactsByIds,
  } = useContactsTable({
    ownerScope,
    pageSize: 25,
    initialSort: { field: 'createdAt', direction: 'desc' },
    globalSearchTerm: searchTerm,
  });

  // Shell-driven search: push term changes into the hook (initial value was
  // seeded via globalSearchTerm above, so skip until it actually changes).
  const lastAppliedTermRef = useRef(searchTerm);
  useEffect(() => {
    if (lastAppliedTermRef.current === searchTerm) return;
    lastAppliedTermRef.current = searchTerm;
    setGlobalSearch(searchTerm);
  }, [searchTerm, setGlobalSearch]);

  // The count badge lives in the shell's control bar now
  useEffect(() => {
    onCountChange?.(totalCount);
  }, [totalCount, onCountChange]);

  /* ---------------- Smart lists (shared with the dashboard) ---------------- */
  const {
    smartLists,
    personalLists,
    globalLists,
    selectedListId,
    setSelectedListId,
    createSmartList,
    updateSmartList,
    deleteSmartList,
    duplicateSmartList,
    setAsDefault: setSmartListAsDefault,
    loading: smartListsLoading,
  } = useSmartLists({ userId: currentUser?.uid, entityType: 'contacts' });

  /* ---------------- Selection ---------------- */
  const [selectedContactIds, setSelectedContactIds] = useState([]);
  const [isAllContactsSelected, setIsAllContactsSelected] = useState(false);
  const [isSelectAllLoading, setIsSelectAllLoading] = useState(false);

  // Selection can't survive a change in what's being selected from
  useEffect(() => {
    setSelectedContactIds([]);
    setIsAllContactsSelected(false);
  }, [activeFilters, searchTerm, selectedUserId]);

  const handleSelectionChange = (contactId) => {
    if (isAllContactsSelected) setIsAllContactsSelected(false);
    setSelectedContactIds((prev) =>
      prev.includes(contactId) ? prev.filter((id) => id !== contactId) : [...prev, contactId]
    );
  };

  const handleSelectAllMatchingFilters = async () => {
    setIsSelectAllLoading(true);
    try {
      const allIds = await fetchAllIds();
      setSelectedContactIds(allIds);
      setIsAllContactsSelected(true);
      toast({ title: `Selected ${allIds.length.toLocaleString()} contacts`, status: 'success', duration: 2000 });
    } catch (error) {
      toast({ title: 'Error selecting contacts', description: error.message, status: 'error', duration: 3000 });
    } finally {
      setIsSelectAllLoading(false);
    }
  };

  const handleClearAllSelection = () => {
    setSelectedContactIds([]);
    setIsAllContactsSelected(false);
  };

  const handleBulkSuccess = () => {
    handleClearAllSelection();
    refreshContacts();
  };

  /* ---------------- Modals ---------------- */
  const { isOpen: isTableCustomOpen, onOpen: onTableCustomOpen, onClose: onTableCustomClose } = useDisclosure();
  const { isOpen: isBulkTagOpen, onOpen: onBulkTagOpen, onClose: onBulkTagClose } = useDisclosure();
  const { isOpen: isBulkTypeOpen, onOpen: onBulkTypeOpen, onClose: onBulkTypeClose } = useDisclosure();
  const { isOpen: isBulkOwnershipOpen, onOpen: onBulkOwnershipOpen, onClose: onBulkOwnershipClose } = useDisclosure();
  const { isOpen: isBulkScopeOpen, onOpen: onBulkScopeOpen, onClose: onBulkScopeClose } = useDisclosure();
  const { isOpen: isBulkDeleteOpen, onOpen: onBulkDeleteOpen, onClose: onBulkDeleteClose } = useDisclosure();
  const { isOpen: isBulkExportOpen, onOpen: onBulkExportOpen, onClose: onBulkExportClose } = useDisclosure();
  const { isOpen: isBulkEmailOpen, onOpen: onBulkEmailOpen, onClose: onBulkEmailClose } = useDisclosure();
  const { isOpen: isBulkSMSOpen, onOpen: onBulkSMSOpen, onClose: onBulkSMSClose } = useDisclosure();
  const { isOpen: isBulkEnrollOpen, onOpen: onBulkEnrollOpen, onClose: onBulkEnrollClose } = useDisclosure();
  const { isOpen: isBulkPostcardOpen, onOpen: onBulkPostcardOpen, onClose: onBulkPostcardClose } = useDisclosure();

  return (
    <Box bg={boxBG} borderWidth="1px" borderColor={borderColor} borderRadius="xl" p={{ base: 3, md: 5 }} shadow="sm">
      <VStack align="stretch" spacing={4}>
        {/* One-click presets */}
        <WorkbenchQuickChips
          entityType="contacts"
          filters={activeFilters}
          onApply={(chipFilters) => {
            setSelectedListId(null);
            applyFilters(chipFilters || []);
          }}
        />

        {/* Full dashboard filter toolbar (quick filters + advanced + smart lists) */}
        <ContactsFilterToolbar
          filters={activeFilters}
          onFiltersChange={applyFilters}
          smartLists={smartLists}
          personalLists={personalLists}
          globalLists={globalLists}
          selectedSmartListId={selectedListId}
          onSelectSmartList={(listId, listFilters) => {
            setSelectedListId(listId);
            if (listFilters) applyFilters(listFilters);
          }}
          onCreateSmartList={createSmartList}
          onUpdateSmartList={updateSmartList}
          onDeleteSmartList={deleteSmartList}
          onDuplicateSmartList={duplicateSmartList}
          onSetDefaultSmartList={setSmartListAsDefault}
          loading={smartListsLoading}
          rightSlot={
            <Tooltip label="Customize table columns">
              <IconButton
                icon={<FiSettings />}
                aria-label="Customize table columns"
                size="sm"
                variant="outline"
                onClick={onTableCustomOpen}
              />
            </Tooltip>
          }
        />

        {/* The dashboard table, rerouted to the workspace */}
        <CustomizableContactsTable
          contacts={contacts}
          totalCount={totalCount}
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          sortBy={contactsSort?.field || tablePreferences.sortBy}
          sortDirection={contactsSort?.direction || tablePreferences.sortDirection}
          onSortChange={(field, direction) => {
            const column = ALL_CONTACTS_COLUMNS.find((c) => c.id === field);
            applySort(field, direction);
            updatePreferences({ sortBy: column?.sortField || field, sortDirection: direction });
          }}
          loading={loading}
          visibleColumns={tablePreferences.visibleColumns}
          columnOrder={tablePreferences.columnOrder}
          selectedContacts={selectedContactIds}
          onSelectionChange={handleSelectionChange}
          onSelectAllMatchingFilters={handleSelectAllMatchingFilters}
          isAllMatchingFiltersSelected={isAllContactsSelected}
          isSelectAllLoading={isSelectAllLoading}
          onClearAllSelection={handleClearAllSelection}
          onContactClick={(contact) => onOpenPerson(contact?.id || contact)}
          onCopyEmail={(email) => {
            navigator.clipboard.writeText(email);
            toast({ title: 'Email copied', status: 'success', duration: 2000 });
          }}
          onCopyPhone={(phone) => {
            navigator.clipboard.writeText(phone);
            toast({ title: 'Phone copied', status: 'success', duration: 2000 });
          }}
          usersMap={usersMap}
          ownerChipViewerId={isCRMAdmin ? null : currentUser?.uid}
        />
      </VStack>

      {/* Bulk bar — same save paths as the dashboard, plus Enroll in Campaign */}
      {selectedContactIds.length > 0 && (
        <BulkContactActionsBar
          count={selectedContactIds.length}
          onUpdateTags={onBulkTagOpen}
          onUpdateTypes={onBulkTypeOpen}
          onTransfer={onBulkOwnershipOpen}
          onDelete={onBulkDeleteOpen}
          onExport={onBulkExportOpen}
          onSendEmail={onBulkEmailOpen}
          onSendSMS={onBulkSMSOpen}
          onSendPostcard={canSendPostcards ? onBulkPostcardOpen : null}
          onClear={handleClearAllSelection}
          isCRMAdmin={isCRMAdmin}
          isAllSelected={isAllContactsSelected}
          dialpadEnabled={dialpadEnabled}
          onEnrollCampaign={onBulkEnrollOpen}
          onSetRecordScope={isCRMAdmin ? onBulkScopeOpen : null}
        />
      )}

      {/* Bulk modals (existing, untouched) */}
      <BulkContactTagModal isOpen={isBulkTagOpen} onClose={onBulkTagClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
      <BulkContactTypeModal isOpen={isBulkTypeOpen} onClose={onBulkTypeClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
      <BulkContactOwnershipModal isOpen={isBulkOwnershipOpen} onClose={onBulkOwnershipClose} selectedContacts={selectedContactIds} crmUsers={crmUsers} onSuccess={handleBulkSuccess} />
      <BulkContactRecordScopeModal isOpen={isBulkScopeOpen} onClose={onBulkScopeClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
      <BulkDeleteContactsModal isOpen={isBulkDeleteOpen} onClose={onBulkDeleteClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
      <BulkExportContactsModal isOpen={isBulkExportOpen} onClose={onBulkExportClose} selectedContacts={selectedContactIds} fetchContactsByIds={fetchContactsByIds} />
      <BulkEmailModal isOpen={isBulkEmailOpen} onClose={onBulkEmailClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
      <BulkSMSModal isOpen={isBulkSMSOpen} onClose={onBulkSMSClose} selectedContacts={selectedContactIds} onSuccess={handleBulkSuccess} />
      <BulkPostcardModal isOpen={isBulkPostcardOpen} onClose={onBulkPostcardClose} recipientType="contacts" ids={selectedContactIds} onSuccess={handleBulkSuccess} />

      {/* NEW: bulk campaign enrollment */}
      <BulkEnrollCampaignModal
        isOpen={isBulkEnrollOpen}
        onClose={onBulkEnrollClose}
        selectedContacts={selectedContactIds}
        onSuccess={handleBulkSuccess}
      />

      <TableCustomizationModal
        isOpen={isTableCustomOpen}
        onClose={onTableCustomClose}
        visibleColumns={tablePreferences.visibleColumns}
        columnOrder={tablePreferences.columnOrder}
        onSave={(updates) => {
          updatePreferences(updates);
          toast({ title: 'Table customization saved', status: 'success', duration: 2000 });
        }}
        onReset={async () => {
          await resetTablePrefs();
          toast({ title: 'Reset to defaults', status: 'success', duration: 2000 });
        }}
        allColumns={ALL_CONTACTS_COLUMNS}
      />
    </Box>
  );
}
